/**
 * Route guard for the Money desk surface (ACCOUNTS-PHASE-1-BUILD-V1).
 * Mirrors RequireBillingEntry: the pure decision lives in
 * decideMoneyDeskAccess (money-desk-view.ts) and keys off the caller's
 * role -- receptionist (the seeded counter role) / coc / owner
 * (supervision reads). Any other role redirects to its own homePath.
 *
 * Role-only UX gate: NO /me flag signal, NO feature flag (internal
 * operator surface, ADR-0016). Backend route auth -- the config-backed C6
 * counter role + coc/owner + the C12 all-branches seam, org/clinic
 * fenced -- remains the real security boundary; an org that retargets the
 * counter role to a role outside this set still works through the
 * backend (recorded gap: the nav approximation hides the link for exotic
 * target roles -- the RequireBillingEntry precedent verbatim).
 */

import { Navigate, Outlet } from 'react-router-dom';
import { useDashboardMeContext } from '@/dashboard/me-context';
import { decideMoneyDeskAccess } from '@/dashboard/money/money-desk-view';
import type { DashboardMe } from '@/dashboard/types';

export interface RequireMoneyDeskViewProps {
  readonly loading: boolean;
  readonly error: string | null;
  readonly me: DashboardMe | null;
  readonly children?: React.ReactNode;
}

/** Pure presentational view - testable via react-dom/server. */
export function RequireMoneyDeskView({
  loading,
  error,
  me,
  children,
}: RequireMoneyDeskViewProps): JSX.Element | null {
  const decision = decideMoneyDeskAccess({ loading, error, me });
  switch (decision.kind) {
    case 'wait':
      return null;
    case 'redirect':
      return <Navigate to={decision.to} replace />;
    case 'render':
      return <>{children ?? <Outlet />}</>;
    default: {
      const _exhaustive: never = decision;
      void _exhaustive;
      return null;
    }
  }
}

export default function RequireMoneyDesk(): JSX.Element | null {
  const ctx = useDashboardMeContext();
  if (!ctx) {
    return <Navigate to="/login" replace />;
  }
  return (
    <RequireMoneyDeskView loading={ctx.loading} error={ctx.error} me={ctx.me} />
  );
}
